import React, { useState } from 'react';
import {
    Alert,
    Button,
    Form,
    Input,
    Modal,
    Popconfirm,
    Space,
    Table,
    Typography,
    message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    useGetCrmEmailTemplatesQuery,
    useCreateCrmEmailTemplateMutation,
    useUpdateCrmEmailTemplateMutation,
    useDeleteCrmEmailTemplateMutation,
} from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';
import { CrmRichTextEditor, EmailBodyHtml, isQuillEmpty } from './CrmRichTextEditor';

const { Text, Paragraph } = Typography;

interface TemplateRow {
    id: number;
    code: string;
    name: string;
    subjectTemplate: string;
    bodyTextTemplate: string;
    bodyHtmlTemplate: string;
}

export const CrmEmailTemplatesTab: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken || '';

    const { data: templates, isLoading } = useGetCrmEmailTemplatesQuery({ jwtToken });
    const [createTemplate, createResult] = useCreateCrmEmailTemplateMutation();
    const [updateTemplate, updateResult] = useUpdateCrmEmailTemplateMutation();
    const [deleteTemplate] = useDeleteCrmEmailTemplateMutation();

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<TemplateRow | null>(null);
    const [form] = Form.useForm();

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        setModalOpen(true);
    };

    const openEdit = (row: TemplateRow) => {
        setEditing(row);
        form.setFieldsValue({
            name: row.name,
            subjectTemplate: row.subjectTemplate,
            bodyHtmlTemplate: row.bodyHtmlTemplate,
        });
        setModalOpen(true);
    };

    const handleSubmit = async (values: {
        name: string;
        subjectTemplate: string;
        bodyHtmlTemplate: string;
    }) => {
        try {
            if (editing) {
                await updateTemplate({ jwtToken, id: editing.id, payload: values }).unwrap();
                message.success(intl.formatMessage({ id: 'crm.template.updated' }));
            } else {
                await createTemplate({ jwtToken, payload: values }).unwrap();
                message.success(intl.formatMessage({ id: 'crm.template.created' }));
            }
            setModalOpen(false);
        } catch (e: any) {
            message.error(e?.data?.message || intl.formatMessage({ id: 'crm.template.saveFailed' }));
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await deleteTemplate({ jwtToken, id }).unwrap();
            message.success(intl.formatMessage({ id: 'crm.template.deleted' }));
        } catch (e: any) {
            message.error(e?.data?.message || intl.formatMessage({ id: 'crm.template.deleteFailed' }));
        }
    };

    const columns = [
        {
            title: <FormattedMessage id="crm.template.colName" defaultMessage="Name" />,
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => <Text strong>{name}</Text>,
        },
        {
            title: <FormattedMessage id="crm.template.colSubject" defaultMessage="Subject" />,
            dataIndex: 'subjectTemplate',
            key: 'subject',
            render: (subject: string) => <Text type="secondary">{subject}</Text>,
        },
        {
            title: <FormattedMessage id="crm.template.colActions" defaultMessage="Actions" />,
            key: 'actions',
            width: 140,
            render: (_: unknown, row: TemplateRow) => (
                <Space size={4}>
                    <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(row)}
                    />
                    <Popconfirm
                        title={<FormattedMessage id="crm.template.deleteConfirm" defaultMessage="Delete this template?" />}
                        onConfirm={() => handleDelete(row.id)}
                        okText={<FormattedMessage id="crm.template.deleteOk" defaultMessage="Delete" />}
                        okButtonProps={{ danger: true }}
                    >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Text type="secondary">
                    <FormattedMessage
                        id="crm.template.intro"
                        defaultMessage="Reusable rich-text templates for one-to-one CRM email. Insert placeholders like {tokens} — they fill in from the recipient when you pick the template."
                        values={{ tokens: '{contactName}, {firstName}, {companyName}, {senderName}' }}
                    />
                </Text>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                    <FormattedMessage id="crm.template.new" defaultMessage="New template" />
                </Button>
            </div>

            <Table
                rowKey="id"
                loading={isLoading}
                dataSource={(templates as TemplateRow[]) || []}
                columns={columns}
                pagination={false}
                size="small"
                expandable={{
                    expandedRowRender: (row: TemplateRow) => (
                        <EmailBodyHtml
                            body={row.bodyHtmlTemplate}
                            style={{ padding: '4px 8px', fontSize: 13 }}
                        />
                    ),
                }}
            />

            <Modal
                open={modalOpen}
                title={
                    editing ? (
                        <FormattedMessage id="crm.template.editTitle" defaultMessage="Edit template" />
                    ) : (
                        <FormattedMessage id="crm.template.newTitle" defaultMessage="New template" />
                    )
                }
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                okText={<FormattedMessage id="crm.template.save" defaultMessage="Save" />}
                okButtonProps={{ loading: createResult.isLoading || updateResult.isLoading }}
                width={720}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="name"
                        rules={[{ required: true }]}
                        label={<FormattedMessage id="crm.template.fieldName" defaultMessage="Template name" />}
                    >
                        <Input placeholder={intl.formatMessage({ id: 'crm.template.fieldNamePlaceholder' })} />
                    </Form.Item>
                    <Form.Item
                        name="subjectTemplate"
                        rules={[{ required: true }]}
                        label={<FormattedMessage id="crm.template.fieldSubject" defaultMessage="Subject" />}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="bodyHtmlTemplate"
                        validateTrigger="onSubmit"
                        rules={[
                            {
                                validator: (_, value) =>
                                    isQuillEmpty(value)
                                        ? Promise.reject(intl.formatMessage({ id: 'crm.email.bodyRequired' }))
                                        : Promise.resolve(),
                            },
                        ]}
                        label={<FormattedMessage id="crm.template.fieldBody" defaultMessage="Message" />}
                    >
                        <CrmRichTextEditor minHeight={260} />
                    </Form.Item>
                    <Alert
                        type="info"
                        showIcon
                        message={
                            <Paragraph style={{ margin: 0 }}>
                                <FormattedMessage
                                    id="crm.template.placeholderHint"
                                    defaultMessage="Placeholders: {tokens}"
                                    values={{ tokens: '{contactName}, {firstName}, {companyName}, {senderName}' }}
                                />
                            </Paragraph>
                        }
                    />
                </Form>
            </Modal>
        </div>
    );
};
